import { useUIStore } from '@/stores'
import { useProject, useThread, useDecisions, useActivity } from '@/hooks'
import { formatDate, formatRelativeTime, getModelShortName, truncate } from '@/utils/helpers'
import { parseTags } from '@/types'
import { Info, FileText, Lightbulb, Activity } from 'lucide-react'
import { useT } from '@/i18n'

export function RightPanel() {
  const { rightPanelTab, setRightPanelTab, activeProjectId, activeThreadId } = useUIStore()
  const t = useT()
  const { data: project } = useProject(activeProjectId)
  const { data: threadData } = useThread(activeThreadId)
  const { data: decisions } = useDecisions(activeProjectId)
  const { data: activity } = useActivity(10)

  const tabs = [
    { id: 'metadata' as const, label: t.panelDetails, icon: Info },
    { id: 'files' as const, label: t.panelSummary, icon: FileText },
    { id: 'decisions' as const, label: t.panelFindings, icon: Lightbulb },
    { id: 'activity' as const, label: t.panelActivity, icon: Activity },
  ]

  return (
    <aside className="right-panel">
      <div className="right-panel-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`tab ${rightPanelTab === tab.id ? 'active' : ''}`}
            onClick={() => setRightPanelTab(tab.id)}
          >
            <tab.icon size={13} />
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      <div className="right-panel-content">
        {rightPanelTab === 'metadata' && (
          <MetadataTab project={project || null} threadData={threadData || null} />
        )}
        {rightPanelTab === 'files' && (
          <SummaryTab threadData={threadData || null} />
        )}
        {rightPanelTab === 'decisions' && (
          <DecisionsTab decisions={decisions || []} />
        )}
        {rightPanelTab === 'activity' && (
          <ActivityTab activity={activity || []} />
        )}
      </div>
    </aside>
  )
}

function MetadataTab({ project, threadData }: { project: any; threadData: any }) {
  const t = useT()
  if (threadData) {
    const tags = parseTags(threadData.tags)
    return (
      <div className="panel-section">
        <h3>{t.panelThreadDetails}</h3>
        <dl className="detail-list">
          <dt>{t.panelTitle}</dt><dd>{threadData.title}</dd>
          <dt>{t.panelProvider}</dt><dd>{threadData.default_provider}</dd>
          <dt>{t.panelModel}</dt><dd>{getModelShortName(threadData.default_model)}</dd>
          <dt>{t.panelMessages}</dt><dd>{threadData.message_count || threadData.messages?.length || 0}</dd>
          <dt>{t.panelStatus}</dt><dd>{threadData.status}</dd>
          <dt>{t.panelPriority}</dt><dd>{threadData.priority}</dd>
          <dt>{t.panelCreated}</dt><dd>{formatDate(threadData.created_at)}</dd>
          <dt>{t.panelUpdated}</dt><dd>{formatDate(threadData.updated_at)}</dd>
        </dl>
        {tags.length > 0 && (
          <div className="panel-tags">
            {tags.map((tg: string) => <span key={tg} className="tag">{tg}</span>)}
          </div>
        )}
      </div>
    )
  }

  if (project) {
    const tags = parseTags(project.tags)
    return (
      <div className="panel-section">
        <h3>{t.panelProjectDetails}</h3>
        <dl className="detail-list">
          <dt>{t.panelName}</dt><dd>{project.name}</dd>
          {project.description && <><dt>{t.panelDescription}</dt><dd>{project.description}</dd></>}
          <dt>{t.panelStatus}</dt><dd>{project.status}</dd>
          <dt>{t.panelPriority}</dt><dd>{project.priority}</dd>
          <dt>{t.panelThreads}</dt><dd>{project.thread_count || 0}</dd>
          <dt>{t.panelFolders}</dt><dd>{project.folder_count || 0}</dd>
          <dt>{t.panelCreated}</dt><dd>{formatDate(project.created_at)}</dd>
        </dl>
        {tags.length > 0 && (
          <div className="panel-tags">
            {tags.map((tg: string) => <span key={tg} className="tag">{tg}</span>)}
          </div>
        )}
      </div>
    )
  }

  return <div className="panel-empty">{t.panelSelectHint}</div>
}

function SummaryTab({ threadData }: { threadData: any }) {
  const t = useT()
  if (!threadData?.summary) {
    return <div className="panel-empty">{t.panelNoSummary}</div>
  }
  return (
    <div className="panel-section">
      <h3>{t.panelThreadSummary}</h3>
      <div className="summary-content">
        {threadData.summary.split('\n').map((line: string, i: number) => (
          <p key={i}>{line || '\u00A0'}</p>
        ))}
      </div>
    </div>
  )
}

function DecisionsTab({ decisions }: { decisions: any[] }) {
  const t = useT()
  if (decisions.length === 0) {
    return <div className="panel-empty">{t.panelNoDecisions}</div>
  }
  return (
    <div className="panel-section">
      <h3>{t.panelKeyFindings}</h3>
      <div className="decisions-list">
        {decisions.map((d) => (
          <div key={d.id} className={`decision-card decision-${d.type}`}>
            <div className="decision-header">
              <span className={`decision-type type-${d.type}`}>{d.type}</span>
              <span className="decision-date">{formatDate(d.created_at)}</span>
            </div>
            <h4>{d.title}</h4>
            <p>{d.content}</p>
          </div>
        ))}
      </div>
    </div>
  )
}

function ActivityTab({ activity }: { activity: any[] }) {
  const t = useT()
  if (activity.length === 0) {
    return <div className="panel-empty">{t.panelNoActivity}</div>
  }
  return (
    <div className="panel-section">
      <h3>{t.panelRecentActivity}</h3>
      <div className="activity-list-panel">
        {activity.map((item) => (
          <div key={item.id} className="activity-item-panel">
            <div className="activity-item-meta">
              <span className="activity-item-time">{formatRelativeTime(item.created_at)}</span>
              <span className="activity-item-model">{getModelShortName(item.model_used)}</span>
            </div>
            <p className="activity-item-text">
              <strong>{item.role === 'user' ? t.you : t.ai}</strong> in {item.thread_title}
            </p>
            <p className="activity-item-preview">{truncate(item.content, 80)}</p>
          </div>
        ))}
      </div>
    </div>
  )
}
